import { useParams } from 'react-router';
import { useConversations } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { cx } from '../../lib/cx';
import { EmptyState, SkeletonList } from '../../ui';
import { ConversationList } from './ConversationList';
import { ConversationThread } from './ConversationThread';

export interface MessagesScreenProps {
  /** `/poruke` za klijenta, `/trener/poruke` za trenera. */
  basePath: string;
}

/**
 * Poruke na dva raspored: na ≥ 768px popis i razgovor stoje jedan uz drugi,
 * ispod toga razgovor zauzima cijeli ekran, a popis je zaseban prikaz.
 * Aktivni razgovor je u URL-u, ne u state-u — povratak i dijeljenje linka rade.
 */
export function MessagesScreen({ basePath }: MessagesScreenProps) {
  const { t } = useTranslation();
  const { conversationId } = useParams<{ conversationId: string }>();
  const conversations = useConversations();

  const activeId = conversationId ?? null;

  return (
    <>
      <div className={cx(activeId && 'hidden md:block')}>
        <PageHeader title={t('messages.title')} subtitle={t('chat.conversations')} />
      </div>

      <QueryBoundary
        query={conversations}
        skeleton={<SkeletonList rows={4} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('chat.noConversations') }}
      >
        {(data) => {
          const active = data.items.find((c) => c.id === activeId) ?? null;

          return (
            <div className="grid min-h-[60dvh] gap-6 md:grid-cols-[minmax(18rem,22rem)_1fr]">
              <aside
                aria-label={t('chat.conversations')}
                className={cx(
                  'min-w-0 md:block md:border-r md:border-line-subtle md:pr-4',
                  activeId ? 'hidden' : 'block',
                )}
              >
                <ConversationList items={data.items} basePath={basePath} activeId={activeId} />
              </aside>

              <div className={cx('flex min-w-0 flex-col', activeId ? 'flex' : 'hidden md:flex')}>
                {active ? (
                  <ConversationThread
                    key={active.id}
                    conversationId={active.id}
                    partnerName={active.partnerName}
                    listPath={basePath}
                  />
                ) : (
                  <EmptyState message={t('chat.selectConversation')} />
                )}
              </div>
            </div>
          );
        }}
      </QueryBoundary>
    </>
  );
}
